import { ResizeSensor2 as BlueprintResizeSensor2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { InteractiveBadge } from 'bones/design-system/components/badge/InteractiveBadge';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { isEqual } from 'bones/utils/isEqual';

interface VoiceboxChatSuggestedPromptsBadgesProps {
  isOpen: boolean;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const VoiceboxChatSuggestedPromptsBadges: React.VFC<
  VoiceboxChatSuggestedPromptsBadgesProps
> = ({ isOpen, sendMessage, sendMessageDisabled, setIsOpen }) => {
  const { config, testIds } = useVoiceboxSchema();
  const { databasesById, settings } = useVoiceboxSettings();

  const examples = React.useMemo(() => {
    return (
      config.examples || databasesById[settings.databaseId]?.examples || []
    );
  }, [config, databasesById, settings]);

  const [showExampleIdx, setShowExampleIdx] = React.useState<number>(1);
  const shownExamples = examples.slice(0, showExampleIdx);
  const hiddenExamples = examples.slice(showExampleIdx);

  const measureRef = React.useRef<HTMLDivElement>(null);
  const handleResize = React.useCallback(() => {
    // istanbul ignore else
    if (measureRef.current) {
      const measureChildren = measureRef.current.children;
      const numChildren = measureChildren.length;

      // istanbul ignore if
      if (!numChildren) {
        setShowExampleIdx(1);
        return;
      }

      const allItems = measureChildren[0];
      if (allItems.scrollWidth <= allItems.clientWidth) {
        setShowExampleIdx(numChildren);
        return;
      }

      setShowExampleIdx(
        new Array(numChildren).fill('').reduce<number>((acc, _, measureIdx) => {
          if (!measureIdx) {
            return acc;
          }

          const child = measureChildren[measureIdx];
          if (child.scrollWidth <= child.clientWidth) {
            return measureIdx;
          }

          return acc;
        }, 1)
      );
    }
  }, [setShowExampleIdx]);

  const didExamplesChange = useDidValueChange(examples, isEqual);
  React.useEffect(() => {
    if (didExamplesChange) {
      setShowExampleIdx(1);
      handleResize();
    }
  }, [didExamplesChange, handleResize]);

  return (
    <>
      {examples.length ? (
        <BlueprintResizeSensor2 onResize={handleResize}>
          <div className="vb-voicebox-chat-suggested-prompts-badges-resize">
            <div
              ref={measureRef}
              className="vb-voicebox-chat-suggested-prompts-badges-measure"
            >
              {new Array(examples.length).fill('').map((_, measureIdx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={measureIdx}
                  className={classNames(
                    'vb-voicebox-chat-suggested-prompts-badges',
                    {
                      'vb-voicebox-chat-suggested-prompts-badges--show-more':
                        measureIdx,
                    }
                  )}
                >
                  {examples
                    .slice(0, measureIdx || undefined)
                    .map((example, exampleIx) => (
                      <InteractiveBadge
                        // eslint-disable-next-line react/no-array-index-key
                        key={exampleIx}
                        size="md"
                        tabIndex={-1}
                        variant="light"
                      >
                        {example}
                      </InteractiveBadge>
                    ))}
                  {measureIdx ? (
                    <InteractiveBadge size="md" tabIndex={-1} variant="light">
                      {examples.length - measureIdx}+
                    </InteractiveBadge>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </BlueprintResizeSensor2>
      ) : null}

      <div
        className={classNames('vb-voicebox-chat-suggested-prompts-badges', {
          'vb-voicebox-chat-suggested-prompts-badges--collapsed': isOpen,
          'vb-voicebox-chat-suggested-prompts-badges--show-more':
            hiddenExamples.length,
        })}
      >
        {shownExamples.map((example, exampleIx) => (
          <InteractiveBadge
            // eslint-disable-next-line react/no-array-index-key
            key={exampleIx}
            onClick={
              sendMessageDisabled ? undefined : () => sendMessage(example)
            }
            size="md"
            tabIndex={!isOpen ? undefined : -1}
            testId={testIds.messages.example.badge(example, exampleIx)}
            variant="light"
          >
            {example}
          </InteractiveBadge>
        ))}
        {hiddenExamples.length ? (
          <InteractiveBadge
            onClick={() => setIsOpen(true)}
            size="md"
            tabIndex={!isOpen ? undefined : -1}
            testId={testIds.messages.example.open}
            variant="light"
          >
            {hiddenExamples.length.toLocaleString()}+
          </InteractiveBadge>
        ) : null}
      </div>
    </>
  );
};
